'use client';

import { create } from 'zustand';
import { devtools, subscribeWithSelector } from 'zustand/middleware';
import { InvoiceAnalysis } from '@/lib/invoice-upload/types/fetch-invoice-analysis-response';
import { fetchInvoiceAnalysis } from '@/lib/invoice-upload/actions/fetch-invoice-analysis';
import useOfferComparisonStore, {
  fetchAndUpdateOffer,
} from '@/app/store/use-offer-comparison-store';
import { ReferenceTariffType } from '@/app/types/reference-tariff-type';
import useEnergyCalculatorStore from '@/app/store/use-energy-calculator-store';
import { getGridOperator } from '@/lib/offer-service/actions/get-grid-operator';
import { differenceInDays, parse } from 'date-fns';
import { getSize } from '@/lib/offer-service/types/customer-type';
import useCustomerStore from '@/app/store/use-customer-store';

const logger = console;

const initialState = {
  analysis: null,
  error: null,
  isLoading: false,
};

export interface InvoiceUploadState {
  analysis: InvoiceAnalysis | null;
  error: string | null;
  isLoading: boolean;
  analyse: (invoices: File[]) => Promise<void>;
  reset: () => void;
  setError: (value: string | null) => void;
}

const useInvoiceUploadStore = create<InvoiceUploadState>()(
  devtools(
    subscribeWithSelector((set) => ({
      ...initialState,
      reset: () => set(initialState),
      setError: (value: string | null) => {
        set({ error: value }, undefined, 'invoiceUpload/setError');
      },
      analyse: async (invoices: File[]) => {
        useInvoiceUploadStore.getState().setError(null);
        set({ isLoading: true }, undefined, 'invoiceUpload/isLoading');
        try {
          const analysis = await getAnalysis(invoices);
          const isGermanCustomer = useCustomerStore.getState().isGermanCustomer;
          const energyData = getEnergyData(analysis, isGermanCustomer);
          const gridOperator = isGermanCustomer
            ? null
            : await getGridOperator(energyData.zipCode);
          const customerType = useEnergyCalculatorStore.getState().customerType;
          const size = getSize(energyData.consumption, customerType);

          set({ analysis }, undefined, 'invoiceUpload/analyse');
          useOfferComparisonStore
            .getState()
            .setCurrentTariffCost(energyData.cost);

          useEnergyCalculatorStore.getState().setCalculatorData(
            {
              size: size,
              consumption: energyData.consumption,
              zipCode: energyData.zipCode,
              ...(gridOperator && {
                gridOperator: gridOperator.name,
                gridOperatorId: gridOperator.id,
                gridAreaId: gridOperator.gridAreaId,
              }),
              ...(energyData.city && { city: energyData.city }),
              ...(energyData.streetName && {
                street: energyData.streetName,
              }),
              ...(energyData.streetNumber && {
                houseNumber: energyData.streetNumber,
              }),
            },
            ReferenceTariffType.invoice
          );
          fetchAndUpdateOffer();
        } catch (err: unknown) {
          const errorMsg = err instanceof Error ? err.message : 'Unknown Error';
          logger.error(errorMsg);
          useInvoiceUploadStore.getState().setError(errorMsg);
        } finally {
          set({ isLoading: false }, undefined, 'invoiceUpload/isLoading');
        }
      },
    }))
  )
);

const calculateEnergyCalculatorData = (
  analysis: InvoiceAnalysis,
  isGermanCustomer: boolean
) => {
  const serviceStartDate = findSingleFieldContent(analysis, 'ServiceStartDate');
  const serviceEndDate = findSingleFieldContent(analysis, 'ServiceEndDate');

  if (!serviceStartDate || !serviceEndDate) {
    return undefined;
  }

  const startDate = parse(serviceStartDate, 'dd.MM.yyyy', new Date());
  const endDate = parse(serviceEndDate, 'dd.MM.yyyy', new Date());
  const daysInService = differenceInDays(endDate, startDate) + 1;
  const consumptionInService = sumNumbers(
    findMultipleFieldContents(analysis, 'KWh_Consumed_POD')
  );
  const costInService = getCostInService(analysis, isGermanCustomer);

  const yearlyConsumption = calculateYearlyValue(
    daysInService,
    consumptionInService
  );
  const yearlyCost = calculateYearlyValue(daysInService, costInService);
  return {
    consumption: yearlyConsumption ? Math.round(yearlyConsumption) : undefined,
    zipCode: findSingleFieldContent(analysis, 'DeliveryAddress_PLZ'),
    city: findSingleFieldContent(analysis, 'DeliveryAddress_City'),
    streetName: findSingleFieldContent(analysis, 'DeliveryAddress_StreetName'),
    streetNumber: findSingleFieldContent(
      analysis,
      'DeliveryAddress_StreetNumber'
    ),
    cost: yearlyCost,
  };
};

function getCostInService(
  analysis: InvoiceAnalysis,
  isGermanCustomer: boolean
): number | null {
  if (isGermanCustomer) {
    const totalCosts = findSingleFieldContent(analysis, 'Total_Costs');
    return totalCosts ? sumNumbers([totalCosts]) : null;
  }

  const energyCosts = findSingleFieldContent(analysis, 'Energiekosten');
  const abgaben = findSingleFieldContent(analysis, 'Abgaben');
  return energyCosts ? sumNumbers([energyCosts, abgaben]) : null;
}

function calculateYearlyValue(daysInService: number, value?: number | null) {
  if (!value) {
    return undefined;
  }
  return parseFloat(((value / daysInService) * 365).toFixed(2));
}

function findSingleFieldContent(
  analysis: InvoiceAnalysis | null,
  key: string
): string | undefined {
  const value = findFieldValue(analysis, key);
  return value && !Array.isArray(value) ? value.content : undefined;
}

function findMultipleFieldContents(
  analysis: InvoiceAnalysis | null,
  key: string
): string[] | undefined {
  const value = findFieldValue(analysis, key);
  return Array.isArray(value) ? value.map((v) => v.content) : undefined;
}

function findFieldValue(analysis: InvoiceAnalysis | null, key: string) {
  return analysis?.documents[0].fields.find((field) => field.key === key)
    ?.value;
}

function sumNumbers(values: (string | undefined)[] | undefined): number | null {
  return (
    values
      ?.map((n) => parseFloat((n ?? '').replace(',', '.')))
      .filter((num) => !isNaN(num))
      .reduce((sum, num) => sum + num, 0) ?? null
  );
}

async function getAnalysis(invoices: File[]) {
  const invoiceAnalysisResponse = await fetchInvoiceAnalysis(invoices);
  if ('error' in invoiceAnalysisResponse) {
    useInvoiceUploadStore
      .getState()
      .setError(invoiceAnalysisResponse.error ?? 'Failed request');
    throw new Error(invoiceAnalysisResponse.error);
  }

  return invoiceAnalysisResponse.data as InvoiceAnalysis;
}

type EnergyData = {
  cost: number;
  consumption: number;
  zipCode: string;
  city?: string;
  streetName?: string;
  streetNumber?: string;
};

function getEnergyData(
  analysis: InvoiceAnalysis,
  isGermanCustomer: boolean
): EnergyData {
  if (analysis.documents[0].energyType !== 'POWER') {
    throw new Error('Invalid energyType');
  }

  const energyData = calculateEnergyCalculatorData(analysis, isGermanCustomer);
  if (!energyData) {
    throw new Error('service start or end dates could not be found');
  }
  if (!energyData.cost) {
    throw new Error('Invoice cost could not be found');
  }
  if (!energyData.consumption) {
    throw new Error('Consumption could not be found');
  }
  if (!energyData.zipCode) {
    throw new Error('Zip code could not be found');
  }
  if (isGermanCustomer && !energyData.city) {
    throw new Error('City could not be found');
  }

  return energyData as EnergyData;
}

export const mapStateToInvoiceData = (state: InvoiceUploadState) => {
  const analysis = state.analysis;

  if (analysis == null) return null;

  return {
    analysisId: analysis.analysisId ?? null,
    firstName: findSingleFieldContent(analysis, 'Customer_FirstName'),
    lastName: findSingleFieldContent(analysis, 'Customer_LastName'),
    gender: null,
    companyName: findSingleFieldContent(analysis, 'Firma_Name'),
    companyVatIN: findSingleFieldContent(analysis, 'Firma_TaxUIDNumber'),
    utility: findSingleFieldContent(analysis, 'Vendor_Name'),
    billingAddress: {
      countryCode: 'AT',
      id: null,
      city: findSingleFieldContent(analysis, 'BillingAddress_City'),
      streetName: findSingleFieldContent(analysis, 'BillingAddress_StreetName'),
      streetNumber: findSingleFieldContent(
        analysis,
        'BillingAddress_StreetNumber'
      ),
      buildingNumber: findSingleFieldContent(
        analysis,
        'BillingAddress_BuildingNumbe'
      ),
      doorNumber: findSingleFieldContent(analysis, 'BillingAddress_DoorNumber'),
      zipCode: findSingleFieldContent(analysis, 'BillingAddress_PLZ'),
      size: null,
      businessSize: null,
      consumption: null,
      isMainResidence: null,
    },
    deliveryAddress: {
      countryCode: 'AT',
      id: null,
      city: findSingleFieldContent(analysis, 'DeliveryAddress_City'),
      streetName: findSingleFieldContent(
        analysis,
        'DeliveryAddress_StreetName'
      ),
      streetNumber: findSingleFieldContent(
        analysis,
        'DeliveryAddress_StreetNumber'
      ),
      buildingNumber: findSingleFieldContent(
        analysis,
        'DeliveryAddress_BuildingNumber'
      ),
      doorNumber: findSingleFieldContent(
        analysis,
        'DeliveryAddress_DoorNumber'
      ),
      zipCode: findSingleFieldContent(analysis, 'DeliveryAddress_PLZ'),
      size: null,
      businessSize: null,
      consumption: null,
      isMainResidence: null,
    },
    pods: findMultipleFieldContents(analysis, 'ZaehlPunktNummer'),
    meterNumber: findSingleFieldContent(analysis, 'Zaehler_Nummer'),
  };
};

export default useInvoiceUploadStore;
